import React, { useMemo } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import type { FaturaPainel, StatusBoleto } from '../types';
import { valorDevido } from '../../../../src/lib/statusBoleto';

interface Props {
  dados: FaturaPainel[];
  modoCompacto?: boolean;
}

export const CORES: Record<StatusBoleto, string> = {
  'Pago': '#5F6C37',
  'Quitado': '#10b981',
  'Em aberto': '#f59e0b',
  'Vencido': '#dc2626',
  'Parcelado': '#F6A937',
  'Cancelado': '#9ca3af',
  'Sem cobrança': '#d1d5db',
  'Sem injeção': '#bae6fd',
  'Cobrança agrupada': '#c4b5fd',
};

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });

const BoletosPorStatusDonut: React.FC<Props> = ({ dados, modoCompacto = false }) => {
  const fatias = useMemo(() => {
    const acc = new Map<StatusBoleto, { qtde: number; valor: number }>();
    dados.forEach((f) => {
      const atual = acc.get(f.statusDerivado) || { qtde: 0, valor: 0 };
      atual.qtde += 1;
      atual.valor += valorDevido(f);
      acc.set(f.statusDerivado, atual);
    });
    return Array.from(acc.entries()).map(([status, v]) => ({ status, ...v }));
  }, [dados]);

  const total = dados.length;

  if (modoCompacto) {
    return (
      <div>
        <h3 className="text-xs font-bold text-gray-800 dark:text-white mb-1">Boletos por Status</h3>
        <div className="relative">
          <ResponsiveContainer width="100%" height={90}>
            <PieChart>
              <Pie data={fatias} dataKey="qtde" nameKey="status" innerRadius={26} outerRadius={40} paddingAngle={2}>
                {fatias.map((f) => (
                  <Cell key={f.status} fill={CORES[f.status]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-base font-bold text-gray-900 dark:text-white">{total}</span>
            <span className="text-[9px] text-gray-400">boletos</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <h3 className="text-sm font-bold text-gray-800 dark:text-white">Boletos por Status</h3>
      <p className="text-[11px] text-gray-400 mb-3">
        "Sem cobrança" = fatura sem boleto emitido ou de valor zero; "Sem injeção" = UC não recebeu compensação SCEE neste ciclo — ambos fora dos totais de emitido e vencido.
      </p>
      <div className="relative">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={fatias} dataKey="qtde" nameKey="status" innerRadius={60} outerRadius={90} paddingAngle={2}>
              {fatias.map((f) => (
                <Cell key={f.status} fill={CORES[f.status]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number, _name, entry: any) => [`${value} boletos · ${fmtBRL(entry.payload.valor)}`, entry.payload.status]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{total}</span>
          <span className="text-[11px] text-gray-400">boletos</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {fatias.map((f) => (
          <div key={f.status} className="flex items-center gap-2 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: CORES[f.status] }} />
            <span className="text-gray-600 dark:text-gray-300 truncate">
              {f.status} · {f.qtde} ({total > 0 ? ((f.qtde / total) * 100).toFixed(0) : 0}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BoletosPorStatusDonut;
